import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';
import { fetchPublicBranches } from '../widget-api/public-branches';
import type { PublicBranchItem } from '../widget-api/public-branches';
import { useAsync } from '../widget-hooks/useAsync';
import { formatPhone, telHref } from '../widget-lib/phone-tel';
import { PATH } from '../widget-lib/path-routes';
import { plainTextSnippet } from '../widget-lib/plain-text-snippet';
import type { PublicOrganizationWithBranchesItem, PublicWidgetResponse } from '../widget-api/public-widget';
import { RichTextBlock } from '../widget-ui/rich-text-block';
import { TextWithPhoneLinks } from '../widget-ui/text-with-phone-links';

const DESCRIPTION_COLLAPSE_AT = 420;

function greetingName(fullName: string | null | undefined): string | null {
  const first = (fullName ?? '').trim().split(/\s+/)[0]?.trim();
  return first || null;
}

/** Бейдж типа организации и «Сеть» — как в шапке embed. */
const ORG_BADGE_CLASS =
  'inline-flex rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]';

type GuestHomeOrgInfo = {
  description: string | null;
  descriptionFormat: 'html' | 'markdown' | 'plain';
  workingHours: string | null;
  phone: string | null;
  typeLabel: string | null;
  /** Режим сети в CRM (`GET /public/widget` → `is_network`); для витрины — несколько филиалов в каталоге. */
  isNetwork: boolean;
};

function buildGuestHomeOrgInfo(
  widgetConfig: PublicWidgetResponse | null,
  guestShowcaseOrg: PublicOrganizationWithBranchesItem | null
): GuestHomeOrgInfo | null {
  if (widgetConfig) {
    const desc = widgetConfig.description?.trim() ? widgetConfig.description : null;
    const wh = widgetConfig.working_hours?.trim() ? widgetConfig.working_hours : null;
    const ph = widgetConfig.phone?.trim() ? widgetConfig.phone : null;
    const label =
      (widgetConfig.organization_type_label ?? '').trim() ||
      (widgetConfig.organization_type ?? '').trim() ||
      null;
    const isNetwork = widgetConfig.is_network === true;
    if (!desc && !wh && !ph && !label && !isNetwork) {
      return null;
    }
    return {
      description: desc,
      descriptionFormat: widgetConfig.description_format ?? 'plain',
      workingHours: wh,
      phone: ph,
      typeLabel: label,
      isNetwork,
    };
  }
  if (guestShowcaseOrg) {
    const single =
      guestShowcaseOrg.branches?.length === 1 ? guestShowcaseOrg.branches[0] : null;
    const rawDesc = guestShowcaseOrg.description?.trim();
    const descPlain = rawDesc ? plainTextSnippet(rawDesc, 8000) : null;
    const wh = single?.working_hours?.trim() ? single.working_hours : null;
    const ph = single?.phone?.trim() ? single.phone : null;
    const label = (guestShowcaseOrg.org_type ?? '').trim() || null;
    const isNetwork = (guestShowcaseOrg.branches_count ?? 0) > 1;
    if (!descPlain && !wh && !ph && !label && !isNetwork) {
      return null;
    }
    return {
      description: descPlain,
      descriptionFormat: 'plain',
      workingHours: wh,
      phone: ph,
      typeLabel: label,
      isNetwork,
    };
  }
  return null;
}

function ContactLines(props: {
  workingHours: string | null;
  phone: string | null;
}) {
  const { workingHours, phone } = props;
  if (!workingHours?.trim() && !phone?.trim()) {
    return null;
  }
  return (
    <div className="space-y-3">
      {workingHours?.trim() ? (
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
            Часы и дни работы
          </div>
          <TextWithPhoneLinks text={workingHours.trim()} />
        </div>
      ) : null}
      {phone?.trim() ? (
        <div className="text-sm">
          <span className="text-[var(--text-muted)]">Телефон: </span>
          <a
            href={telHref(phone.trim())}
            className="font-medium text-[var(--accent)] underline-offset-2 hover:underline"
          >
            {formatPhone(phone.trim())}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function BranchContactTabs({ branches, branchPlural }: { branches: PublicBranchItem[]; branchPlural: string }) {
  const [activeId, setActiveId] = useState(() => branches[0]?.id ?? 0);

  useEffect(() => {
    const first = branches[0]?.id;
    if (first == null) {
      return;
    }
    setActiveId((prev) => (branches.some((b) => b.id === prev) ? prev : first));
  }, [branches]);

  const active = branches.find((b) => b.id === activeId) ?? branches[0];
  if (!active) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold tracking-tight text-[var(--text)]">{branchPlural}</h2>
      <div
        role="tablist"
        aria-label={branchPlural}
        className="flex flex-wrap gap-2 border-b border-[var(--border)] pb-2"
      >
        {branches.map((b) => {
          const selected = b.id === active.id;
          return (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={
                selected
                  ? 'rounded-t-sm border border-b-0 border-[var(--border)] bg-[var(--bg-soft)] px-3 py-1.5 text-[13px] font-medium text-[var(--text)]'
                  : 'rounded-sm border border-transparent px-3 py-1.5 text-[13px] font-medium text-[var(--text-muted)] hover:bg-[var(--bg-soft)]'
              }
              onClick={() => setActiveId(b.id)}
            >
              {b.name}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" className="space-y-3">
        {active.address?.trim() ? (
          <p className="text-sm">
            <span className="text-[var(--text-muted)]">Адрес: </span>
            <span className="text-[var(--text)]">{active.address.trim()}</span>
          </p>
        ) : null}
        <ContactLines workingHours={active.working_hours} phone={active.phone} />
      </div>
    </div>
  );
}

function GuestAboutStudioSection({
  info,
  hideOrgContact
}: {
  info: GuestHomeOrgInfo;
  hideOrgContact: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const longDescription = (info.description?.length ?? 0) > DESCRIPTION_COLLAPSE_AT;
  const showCollapsed = longDescription && !expanded;

  const descInner =
    info.description && info.descriptionFormat === 'plain' ? (
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-muted)]">{info.description}</p>
    ) : info.description ? (
      <RichTextBlock
        html={info.description}
        format={info.descriptionFormat}
        className="text-sm leading-relaxed text-[var(--text-muted)] [&_a]:text-[var(--accent)] [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
      />
    ) : null;

  const showContact = !hideOrgContact && (info.workingHours?.trim() || info.phone?.trim());

  return (
    <section className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight text-[var(--text)]">О студии</h1>
      {info.typeLabel || info.isNetwork ? (
        <div className="flex flex-wrap gap-1.5">
          {info.typeLabel ? <span className={ORG_BADGE_CLASS}>{info.typeLabel}</span> : null}
          {info.isNetwork ? <span className={ORG_BADGE_CLASS}>Сеть</span> : null}
        </div>
      ) : null}
      {info.description ? (
        <div className="space-y-2">
          <div className={showCollapsed ? 'max-h-44 overflow-hidden' : ''}>{descInner}</div>
          {longDescription ? (
            <button
              type="button"
              className="text-xs font-medium text-[var(--accent)] underline-offset-2 hover:underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Свернуть' : 'Показать полностью'}
            </button>
          ) : null}
        </div>
      ) : null}
      {showContact ? (
        <ContactLines workingHours={info.workingHours} phone={info.phone} />
      ) : null}
    </section>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    accessToken,
    me,
    brandEmbedAllowed,
    canFetchWidget,
    widgetConfig,
    guestShowcaseBrowse,
    guestShowcaseOrg,
    organizationId: ctxOrganizationId,
    effectiveModules
  } = useEmbedBootstrap();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const multiOrg = Boolean(accessToken && me && (me.memberships?.length ?? 0) > 1);
  /**
   * Тот же экран «О студии», что и до входа: подписанный виджет (`widgetConfig`) или витрина глобального виджета.
   * После входа JWT сохраняется, URL с org_slug/key остаётся — показываем тот же контент.
   */
  const showAboutStudioHome =
    Boolean(widgetConfig && canFetchWidget) || Boolean(guestShowcaseBrowse && guestShowcaseOrg);

  const branchesPick = widgetConfig?.branches ?? guestShowcaseOrg?.branches ?? [];
  const showSalonsButton = showAboutStudioHome && branchesPick.length >= 1;

  const organizationId = widgetConfig?.organization_id ?? guestShowcaseOrg?.id ?? ctxOrganizationId;

  const { data: allPublicBranches, loading: branchesLoading } = useAsync(
    () =>
      showAboutStudioHome && organizationId != null ? fetchPublicBranches() : Promise.resolve(null),
    [showAboutStudioHome, organizationId]
  );

  const orgBranches = useMemo((): PublicBranchItem[] => {
    if (!allPublicBranches || organizationId == null) {
      return [];
    }
    return allPublicBranches.filter((b) => b.organization_id === organizationId);
  }, [allPublicBranches, organizationId]);

  const orderedBranches = useMemo((): PublicBranchItem[] => {
    if (widgetConfig?.branches?.length) {
      const order = new Map(widgetConfig.branches.map((b, i) => [b.id, i]));
      return [...orgBranches].sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999));
    }
    return [...orgBranches].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [orgBranches, widgetConfig?.branches]);

  const guestOrgInfo = useMemo(
    () => (showAboutStudioHome ? buildGuestHomeOrgInfo(widgetConfig, guestShowcaseOrg) : null),
    [showAboutStudioHome, widgetConfig, guestShowcaseOrg]
  );

  const authedOrgInfo = useMemo(() => {
    if (!widgetConfig) return null;
    return buildGuestHomeOrgInfo(widgetConfig, null);
  }, [widgetConfig]);

  const branchLabels = useBranchLabels();

  const showBranchTabs = orderedBranches.length > 1;
  const singleBranchRow = orderedBranches.length === 1 ? orderedBranches[0] : null;

  const mergedSalonContact =
    singleBranchRow && guestOrgInfo
      ? {
          workingHours: singleBranchRow.working_hours?.trim()
            ? singleBranchRow.working_hours
            : guestOrgInfo.workingHours,
          phone: singleBranchRow.phone?.trim() ? singleBranchRow.phone : guestOrgInfo.phone
        }
      : singleBranchRow
        ? {
            workingHours: singleBranchRow.working_hours,
            phone: singleBranchRow.phone
          }
        : null;

  /** Несколько филиалов — часы/телефоны только в табах; один — блок «Салон» с данными филиала и fallback на организацию. */
  const hideOrgContactInAbout =
    showBranchTabs || Boolean(singleBranchRow && !branchesLoading);

  if (showAboutStudioHome) {
    return (
      <div className="space-y-6">
        {guestOrgInfo ? (
          <GuestAboutStudioSection info={guestOrgInfo} hideOrgContact={hideOrgContactInAbout} />
        ) : null}

        {branchesLoading && showBranchTabs ? (
          <p className="text-sm text-[var(--text-muted)]">
            Загружаем данные по {branchLabels.dativePluralLc}…
          </p>
        ) : null}

        {!branchesLoading && showBranchTabs ? (
          <BranchContactTabs branches={orderedBranches} branchPlural={branchLabels.plural} />
        ) : null}

        {!branchesLoading && !showBranchTabs && singleBranchRow ? (
          <div className="space-y-3">
            <h2 className="text-base font-semibold tracking-tight text-[var(--text)]">{branchLabels.singular}</h2>
            {singleBranchRow.address?.trim() ? (
              <p className="text-sm">
                <span className="text-[var(--text-muted)]">Адрес: </span>
                <span className="text-[var(--text)]">{singleBranchRow.address.trim()}</span>
              </p>
            ) : null}
            <ContactLines workingHours={mergedSalonContact?.workingHours ?? null} phone={mergedSalonContact?.phone ?? null} />
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2 pt-1 sm:flex sm:flex-wrap">
          <Button type="button" onClick={() => navigate(`${PATH.schedule}${suffix}`)}>
            Расписание
          </Button>
          {showSalonsButton ? (
            <Button type="button" variant="secondary" onClick={() => navigate(`${PATH.branches}${suffix}`)}>
              {branchLabels.plural}
            </Button>
          ) : null}
          {!accessToken ? (
            <Button type="button" variant="secondary" onClick={() => navigate(`${PATH.signIn}${suffix}`)}>
              Вход
            </Button>
          ) : null}
        </div>

        {multiOrg && brandEmbedAllowed === true ? (
          <p className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-xs text-[var(--text-muted)]">
            Режим встраивания для этой студии: переключение списка организаций скрыто. Контекст соответствует ссылке виджета.
          </p>
        ) : null}
      </div>
    );
  }

  const modules = effectiveModules ?? me?.modules ?? null;
  const hello = accessToken ? greetingName(me?.full_name) : null;

  return (
    <div className="space-y-5 w-full min-w-0">
      {hello ? (
        <p className="text-sm text-[var(--text-muted)]">Здравствуйте, {hello}.</p>
      ) : null}

      <div className="flex flex-wrap gap-2 border-t border-white/10 pb-2.5 pt-2">
        <Button type="button" className="shrink-0" onClick={() => navigate(`${PATH.schedule}${suffix}`)}>
          Расписание
        </Button>
        <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.book}${suffix}`)}>
          Записаться
        </Button>
        <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.services}${suffix}`)}>
          Услуги
        </Button>
        {modules?.fitness === true ? (
          <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.trainers}${suffix}`)}>
            Тренеры
          </Button>
        ) : null}
        {modules?.subscriptions === true ? (
          <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.subscriptionPlans}${suffix}`)}>
            Абонементы
          </Button>
        ) : null}
        {accessToken ? (
          <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.bookings}${suffix}`)}>
            Мои записи
          </Button>
        ) : null}
        {accessToken ? (
          <Button type="button" variant="secondary" className="shrink-0" onClick={() => navigate(`${PATH.mySubscriptions}${suffix}`)}>
            Мои абонементы
          </Button>
        ) : null}
        {accessToken ? (
          <Button type="button" variant="ghost" className="shrink-0" onClick={() => navigate(`${PATH.account}${suffix}`)}>
            Личный кабинет
          </Button>
        ) : null}
      </div>

      {authedOrgInfo ? (
        <GuestAboutStudioSection info={authedOrgInfo} hideOrgContact={hideOrgContactInAbout} />
      ) : null}

      {!branchesLoading && showBranchTabs ? (
        <BranchContactTabs branches={orderedBranches} branchPlural={branchLabels.plural} />
      ) : null}

      {!branchesLoading && !showBranchTabs && singleBranchRow ? (
        <div className="space-y-3">
          <h2 className="text-base font-semibold tracking-tight text-[var(--text)]">{branchLabels.singular}</h2>
          {singleBranchRow.address?.trim() ? (
            <p className="text-sm">
              <span className="text-[var(--text-muted)]">Адрес: </span>
              <span className="text-[var(--text)]">{singleBranchRow.address.trim()}</span>
            </p>
          ) : null}
          <ContactLines workingHours={mergedSalonContact?.workingHours ?? null} phone={mergedSalonContact?.phone ?? null} />
        </div>
      ) : null}

      {multiOrg && brandEmbedAllowed === true ? (
        <p className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2 text-xs text-[var(--text-muted)]">
          Режим встраивания для этой студии: переключение списка организаций скрыто.
        </p>
      ) : null}
    </div>
  );
}
